/**
 * useFilteredIssues — pure derivation of filtered issues from a list + filter state.
 *
 * No useState, no useEffect. Just a useMemo that recomputes when inputs change.
 */

import { useMemo } from 'react';
import type { Issue } from '../api/work';
import type { FilterState } from '../components/work/shared/FilterBar';

/**
 * Pure filter function — no React, no hooks, fully testable.
 */
export function filterIssues(
  issues: Issue[],
  filters: FilterState,
  currentUserId: string | null,
): Record<string, Issue> {
  let filtered = issues;

  // Text search
  if (filters.searchText) {
    const searchLower = filters.searchText.toLowerCase();
    filtered = filtered.filter(issue => {
      // Check for special syntax (type:, priority:, status:)
      if (searchLower.includes(':')) {
        const [key, value] = searchLower.split(':', 2);
        if (key === 'type' && value) return issue.type === value.trim();
        if (key === 'priority' && value) return issue.priority === value.trim();
        if (key === 'status' && value) return issue.status === value.trim();
      }
      return (
        issue.title.toLowerCase().includes(searchLower) ||
        issue.description?.toLowerCase().includes(searchLower)
      );
    });
  }

  // Type filter
  if (filters.types.length > 0) {
    filtered = filtered.filter(issue => filters.types.includes(issue.type));
  }

  // Priority filter
  if (filters.priorities.length > 0) {
    filtered = filtered.filter(issue => filters.priorities.includes(issue.priority));
  }

  // My issues filter
  if (filters.showMyIssues) {
    filtered = filtered.filter(issue => issue.assignee?.id === currentUserId);
  }

  // Unassigned filter
  if (filters.showUnassigned) {
    filtered = filtered.filter(issue => issue.assignee === null);
  }

  // Initiative filter
  if (filters.initiatives.length > 0) {
    filtered = filtered.filter(issue =>
      issue.initiative_id && filters.initiatives.includes(issue.initiative_id),
    );
  }

  // Team filter
  if (filters.teams.length > 0) {
    filtered = filtered.filter(issue =>
      issue.team_ids && issue.team_ids.some((teamId: string) => filters.teams.includes(teamId)),
    );
  }

  // Assignee filter
  if (filters.assignees.length > 0) {
    filtered = filtered.filter(issue =>
      issue.assignee && filters.assignees.includes(issue.assignee.id),
    );
  }

  // Label filter
  if (filters.labels.length > 0) {
    filtered = filtered.filter(issue =>
      issue.labels && issue.labels.some(label => filters.labels.includes(label)),
    );
  }

  // Date range filters
  if (filters.startDateRange) {
    const { from, to } = filters.startDateRange;
    filtered = filtered.filter(issue => {
      if (!issue.start_date) return false;
      const startDate = new Date(issue.start_date);
      return startDate >= from && startDate <= to;
    });
  }

  if (filters.dueDateRange) {
    const { from, to } = filters.dueDateRange;
    filtered = filtered.filter(issue => {
      if (!issue.due_date) return false;
      const dueDate = new Date(issue.due_date);
      return dueDate >= from && dueDate <= to;
    });
  }

  // Convert to Record
  const result: Record<string, Issue> = {};
  for (const issue of filtered) {
    result[issue.id] = issue;
  }
  return result;
}

/**
 * Hook: memoized filter derivation.
 */
export function useFilteredIssues(
  issues: Issue[],
  filters: FilterState,
  currentUserId: string | null,
): Record<string, Issue> {
  return useMemo(
    () => filterIssues(issues, filters, currentUserId),
    [issues, filters, currentUserId],
  );
}
